import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Checkbox,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ORGANIZATION_SERVER, USER_SERVER } from '../../Config';

import axios from 'axios';
import { useSnackbar } from 'notistack';

// TransferList utils
function not(a, b) {
  return a.filter((value) => b.findIndex((v) => v._id === value._id) === -1);
}

function intersection(a, b) {
  return a.filter((value) => b.findIndex((v) => v._id === value._id) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

const ProjectUserAccessDialog = ({ open, handleClose, projectId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [checked, setChecked] = useState([]);
  const [project, setProject] = useState(null); // 추가: project 상태

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const fetchProject = useCallback(async () => {
    if (!projectId) return;

    try {
      const [projectRes, usersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_SERVER}${ORGANIZATION_SERVER}/group/${projectId}`),
        axios.get(`${process.env.REACT_APP_SERVER}${USER_SERVER}/all/${projectId}`),
      ]);

      const fetchedProject = projectRes.data;
      const userList = usersRes.data;

      // project 상태 저장
      setProject(fetchedProject);

      const projectUserIds = new Set(fetchedProject.group || []);

      const leftList = userList.filter((user) => !projectUserIds.has(user._id));
      const rightList = userList.filter((user) => projectUserIds.has(user._id));

      setLeft(leftList);
      setRight(rightList);
      setChecked([]);
    } catch (err) {
      enqueueSnackbar('프로젝트 또는 사용자 정보를 불러오지 못했습니다.', { variant: 'error' });
    }
  }, [projectId, enqueueSnackbar]);

  useEffect(() => {
    if (!open || !projectId) return;
    fetchProject();
  }, [open, projectId, fetchProject]);

  const handleToggle = (value) => () => {
    const currentIndex = checked.findIndex((v) => v._id === value._id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
    setChecked([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked([]);
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked([]);
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);
    setChecked([]);
  };

  const filterItems = (items, text) => {
    const t = (text || '').trim().toLowerCase();
    if (!t) return items;
    return items.filter(
      (item) => (item.name || '').toLowerCase().includes(t) || (item.groupname || '').toLowerCase().includes(t)
    );
  };

  const customList = (title, items, searchKey, showReload = false, bgColor = 'white') => {
    const text = searchKey === 'left' ? searchLeft : searchRight;
    const filtered = filterItems(items, text);

    const visibleChecked = intersection(checked, filtered);

    const handleChangeSearch = (e) => {
      const v = e.target.value;
      if (searchKey === 'left') {
        setSearchLeft(v);
      } else {
        setSearchRight(v);
      }
    };

    return (
      <Card
        sx={{
          height: 520,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: bgColor,
          border: '1px solid #ccc',
        }}
      >
        <CardHeader
          sx={{ px: 2, py: 1 }}
          avatar={
            <Checkbox
              onClick={() => {
                const visibleItems = filterItems(items, text);
                const isAllSelected = visibleItems.length > 0 && visibleItems.length === checked.length;
                if (isAllSelected) {
                  setChecked(not(checked, filtered));
                } else {
                  setChecked(union(checked, visibleItems));
                }
              }}
              checked={filtered.length > 0 && filtered.length === visibleChecked.length}
              indeterminate={visibleChecked.length > 0 && visibleChecked.length < filtered.length}
              disabled={filtered.length === 0}
              inputProps={{ 'aria-label': 'all items selected' }}
            />
          }
          title={title}
          subheader={`${visibleChecked.length}/${filtered.length} 선택됨`}
        />
        <Divider />

        {/* 검색 + 재조회 버튼 영역 */}
        <CardContent
          sx={{
            py: 0,
            px: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <TextField
              size="small"
              placeholder="이름 또는 기관명 검색"
              value={text}
              onChange={handleChangeSearch}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: { fontSize: '0.875rem' },
              }}
            />
            {showReload && (
              <Button size="small" variant="outlined" color="primary" onClick={fetchUsersOnly} sx={{ minWidth: 80 }}>
                재조회
              </Button>
            )}
          </Box>
        </CardContent>

        <CardContent
          sx={{
            height: 520,
            overflowY: 'scroll',
          }}
        >
          <div
            style={{
              height: 'auto',
              width: '100%',
            }}
          >
            {filtered.map((value) => {
              const labelId = `transfer-list-item-${value._id}`;
              return (
                <ListItemButton
                  key={value._id}
                  role="listitem"
                  onClick={handleToggle(value)}
                  sx={{
                    px: 1.5,
                    py: 0.3,
                    minHeight: 60,
                    height: 'auto',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.findIndex((v) => v._id === value._id) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={labelId}
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {value.name}
                        </Typography>
                        {value.groupname ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                          >
                            {'| ' + value.groupname}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                    secondary={
                      value.projectName ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            lineHeight: 1,
                            display: 'block',
                          }}
                        >
                          {value.projectName}
                        </Typography>
                      ) : null
                    }
                  />
                </ListItemButton>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const handleSave = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_SERVER}${ORGANIZATION_SERVER}/group/access/${projectId}`, {
        group: right.map((u) => u._id),
      });
      enqueueSnackbar('프로젝트 권한이 업데이트되었습니다.', { variant: 'success' });
      await fetchProject();
    } catch (err) {
      enqueueSnackbar('권한 업데이트에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleCancel = () => {
    setLeft([]);
    setRight([]);
    setChecked([]);
    handleClose();
  };

  const fetchUsersOnly = useCallback(async () => {
    if (!open) return;

    try {
      const usersRes = await axios.get(`${process.env.REACT_APP_SERVER}${USER_SERVER}/all/${projectId}`);
      const userList = usersRes.data;

      // 현재 프로젝트에 이미 권한 있는 유저 IDs
      const projectUserIds = new Set(right.map((u) => u._id));

      const newLeft = userList.filter((user) => !projectUserIds.has(user._id));

      setLeft(newLeft);
    } catch (err) {
      enqueueSnackbar('전체 사용자 목록을 새로 불러오지 못했습니다.', { variant: 'error' });
    }
  }, [open, right, enqueueSnackbar]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          borderRadius: 2,
        },
        '& .MuiDialogContent-root': {
          bgcolor: 'background.default',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          px: 3,
          pt: 2,
          pb: 1,
          fontWeight: 600,
          fontSize: '1.125rem',
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        {/* 타이틀 라인 */}
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          프로젝트 권한부여
        </Typography>

        {project && (
          <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
            {project.projectName} / {project.name}
          </Typography>
        )}

        {/* 닫기 버튼 */}
        <IconButton
          size="small"
          onClick={handleClose}
          aria-label="Close dialog"
          sx={{ position: 'absolute', top: 16, right: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 3,
          py: 2,
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: 'background.default',
        }}
      >
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            boxSizing: 'border-box',
          }}
          columns={11}
        >
          <Grid size={{ xs: 5 }}>{customList('전체 사용자', left, 'left', true, '#f0f0f0')}</Grid>

          <Grid
            size={{ xs: 1 }}
            sx={{
              height: '100%',
              py: 0,
              pr: 0.5,
              mt: 1.5,
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <Button
              sx={{ my: 1 }}
              variant="outlined"
              size="large"
              onClick={handleAllRight}
              disabled={left.length === 0}
              aria-label="Move all to access list"
            >
              ≫
            </Button>
            <Button
              sx={{ my: 1 }}
              variant="outlined"
              size="large"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="Move selected to access list"
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 1 }}
              variant="outlined"
              size="large"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="Remove selected from access list"
            >
              &lt;
            </Button>
            <Button
              sx={{ my: 1 }}
              variant="outlined"
              size="large"
              onClick={handleAllLeft}
              disabled={right.length === 0}
              aria-label="Remove all from access list"
            >
              ≪
            </Button>
          </Grid>

          <Grid size={{ xs: 5 }}>{customList('권한이 있는 사용자', right, 'right', false, '#e1f5fe')}</Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: 'flex-end',
          bgcolor: 'background.paper',
        }}
      >
        <Button variant="outlined" color="inherit" onClick={handleCancel}>
          취소
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectUserAccessDialog;
